import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Briefcase,
  MapPin,
  Clock,
  DollarSign,
  Search,
  Plus,
  Building2,
  Calendar,
  Send,
  Eye,
  Edit,
  Trash2,
  Loader2,
  Filter,
  Wifi,
  CheckCircle,
  FileText,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobBoard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [activeTab, setActiveTab] = useState('browse');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [remoteOnly, setRemoteOnly] = useState(false);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Job form
  const [jobForm, setJobForm] = useState({
    title: '',
    description: '',
    company_name: '',
    location: '',
    job_type: 'full_time',
    category: 'veterinary',
    salary_min: '',
    salary_max: '',
    salary_type: 'yearly',
    requirements: '',
    benefits: '',
    contact_email: '',
    application_url: '',
    remote_friendly: false,
    experience_level: 'entry'
  });
  
  // Application form
  const [applicationForm, setApplicationForm] = useState({
    cover_letter: '',
    phone: ''
  });

  const categories = [
    { value: 'veterinary', label: 'Veterinary' },
    { value: 'grooming', label: 'Grooming' },
    { value: 'training', label: 'Training' },
    { value: 'walking', label: 'Dog Walking' },
    { value: 'sitting', label: 'Pet Sitting' },
    { value: 'boarding', label: 'Boarding' },
    { value: 'retail', label: 'Retail' },
    { value: 'rescue', label: 'Rescue/Shelter' },
    { value: 'other', label: 'Other' }
  ];

  const jobTypes = [
    { value: 'full_time', label: 'Full Time' },
    { value: 'part_time', label: 'Part Time' },
    { value: 'contract', label: 'Contract' },
    { value: 'freelance', label: 'Freelance' },
    { value: 'volunteer', label: 'Volunteer' }
  ];

  useEffect(() => {
    fetchJobs();
    if (user) {
      fetchMyListings();
      fetchMyApplications();
    }
  }, [user, searchQuery, categoryFilter, typeFilter, remoteOnly]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (categoryFilter) params.append('category', categoryFilter);
      if (typeFilter) params.append('job_type', typeFilter);
      if (remoteOnly) params.append('remote_only', 'true');
      
      const response = await axios.get(`${API}/jobs?${params.toString()}`);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyListings = async () => {
    try {
      const response = await axios.get(`${API}/jobs/my-listings`);
      setMyListings(response.data);
    } catch (error) {
      console.error('Error fetching my listings:', error);
    }
  };

  const fetchMyApplications = async () => {
    try {
      const response = await axios.get(`${API}/jobs/applications/mine`);
      setMyApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  const handleCreateJob = async () => {
    if (!jobForm.title || !jobForm.description || !jobForm.company_name || !jobForm.contact_email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/jobs`, {
        ...jobForm,
        salary_min: jobForm.salary_min ? parseInt(jobForm.salary_min) : null,
        salary_max: jobForm.salary_max ? parseInt(jobForm.salary_max) : null,
        requirements: jobForm.requirements ? jobForm.requirements.split('\n').filter(Boolean) : [],
        benefits: jobForm.benefits ? jobForm.benefits.split('\n').filter(Boolean) : []
      });
      
      toast.success('Job posted!');
      setCreateDialogOpen(false);
      resetJobForm();
      fetchJobs();
      fetchMyListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post job');
    } finally {
      setSaving(false);
    }
  };

  const handleApply = async () => {
    if (!selectedJob) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/jobs/${selectedJob.id}/apply`, {
        job_id: selectedJob.id,
        cover_letter: applicationForm.cover_letter,
        phone: applicationForm.phone
      });
      
      toast.success('Application submitted!');
      setApplyDialogOpen(false);
      setApplicationForm({ cover_letter: '', phone: '' });
      fetchMyApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job posting?')) return;
    
    try {
      await axios.delete(`${API}/jobs/${jobId}`);
      toast.success('Job deleted');
      fetchMyListings();
      fetchJobs();
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const resetJobForm = () => {
    setJobForm({
      title: '',
      description: '',
      company_name: '',
      location: '',
      job_type: 'full_time',
      category: 'veterinary',
      salary_min: '',
      salary_max: '',
      salary_type: 'yearly',
      requirements: '',
      benefits: '',
      contact_email: user?.email || '',
      application_url: '',
      remote_friendly: false,
      experience_level: 'entry'
    });
  };

  const formatSalary = (job) => {
    if (!job.salary_min && !job.salary_max) return null;
    const type = job.salary_type === 'hourly' ? '/hr' : '/yr';
    if (job.salary_min && job.salary_max) {
      return `$${job.salary_min.toLocaleString()} - $${job.salary_max.toLocaleString()}${type}`;
    }
    if (job.salary_min) return `From $${job.salary_min.toLocaleString()}${type}`;
    return `Up to $${job.salary_max.toLocaleString()}${type}`;
  };

  const hasApplied = (jobId) => {
    return myApplications.some(app => app.job_id === jobId);
  };

  // Job Card Component
  const JobCard = ({ job, showActions = true }) => (
    <Card className="hover:shadow-md transition-shadow" data-testid={`job-card-${job.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="capitalize text-xs">
                {job.category.replace('_', ' ')}
              </Badge>
              <Badge className="text-xs capitalize">
                {job.job_type.replace('_', ' ')}
              </Badge>
              {job.remote_friendly && (
                <Badge variant="secondary" className="text-xs">
                  <Wifi className="w-3 h-3 mr-1" />
                  Remote
                </Badge>
              )}
            </div>
            
            <h3 className="font-heading text-lg font-medium mb-1">{job.title}</h3>
            
            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
              <span className="flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                {job.company_name}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {job.location}
              </span>
            </div>
            
            {formatSalary(job) && (
              <div className="flex items-center gap-1 text-sm font-medium text-green-600 mb-2">
                <DollarSign className="w-4 h-4" />
                {formatSalary(job)}
              </div>
            )}
            
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {job.description}
            </p>
            
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" />
              Posted {format(new Date(job.created_at), 'MMM d, yyyy')}
              {job.application_count > 0 && (
                <span className="ml-2 flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {job.application_count} applicants
                </span>
              )}
            </div>
          </div>
          
          {showActions && (
            <div className="flex flex-col gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setSelectedJob(job);
                  setViewDialogOpen(true);
                }}
              >
                <Eye className="w-4 h-4 mr-1" />
                View
              </Button>
              {user && (
                hasApplied(job.id) ? (
                  <Button size="sm" disabled variant="secondary">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Applied
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => {
                      setSelectedJob(job);
                      setApplyDialogOpen(true);
                    }}
                  >
                    <Send className="w-4 h-4 mr-1" />
                    Apply
                  </Button>
                )
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-heading font-medium mb-4 flex items-center gap-3">
            <Briefcase className="w-10 h-10" />
            Pet Industry Job Board
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Find your dream job in the pet industry or post opportunities for qualified professionals.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Search and Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label>Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search jobs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              
              <div className="w-40">
                <Label>Category</Label>
                <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-36">
                <Label>Type</Label>
                <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {jobTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  id="remote"
                  checked={remoteOnly}
                  onCheckedChange={setRemoteOnly}
                />
                <Label htmlFor="remote" className="cursor-pointer">Remote Only</Label>
              </div>
              
              {user && (
                <Button onClick={() => {
                  resetJobForm();
                  setCreateDialogOpen(true);
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Post a Job
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs for Browse / My Listings / My Applications */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="browse">
              <Search className="w-4 h-4 mr-2" />
              Browse Jobs ({jobs.length})
            </TabsTrigger>
            {user && (
              <>
                <TabsTrigger value="listings">
                  <FileText className="w-4 h-4 mr-2" />
                  My Listings ({myListings.length})
                </TabsTrigger>
                <TabsTrigger value="applications">
                  <Send className="w-4 h-4 mr-2" />
                  My Applications ({myApplications.length})
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Browse Jobs */}
          <TabsContent value="browse">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : jobs.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Briefcase className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="text-xl font-heading mb-2">No Jobs Found</h3>
                  <p className="text-muted-foreground mb-4">
                    {searchQuery || categoryFilter || typeFilter
                      ? 'Try adjusting your filters'
                      : 'Be the first to post a job opportunity!'}
                  </p>
                  {user && (
                    <Button onClick={() => setCreateDialogOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Post a Job
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {jobs.map(job => (
                  <JobCard key={job.id} job={job} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Listings */}
          <TabsContent value="listings">
            {myListings.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <FileText className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="text-xl font-heading mb-2">No Job Listings Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Post your first job to find qualified candidates
                  </p>
                  <Button onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Post a Job
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {myListings.map(job => (
                  <Card key={job.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant={job.is_active ? 'default' : 'secondary'}>
                              {job.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <Badge variant="outline" className="capitalize">
                              {job.category.replace('_', ' ')}
                            </Badge>
                          </div>
                          <h3 className="font-heading text-lg font-medium">{job.title}</h3>
                          <p className="text-sm text-muted-foreground mb-2">
                            {job.application_count} applications received
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Posted {format(new Date(job.created_at), 'MMM d, yyyy')}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedJob(job);
                              setViewDialogOpen(true);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive"
                            onClick={() => handleDeleteJob(job.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Applications */}
          <TabsContent value="applications">
            {myApplications.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Send className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="text-xl font-heading mb-2">No Applications Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Start applying to jobs that interest you
                  </p>
                  <Button onClick={() => setActiveTab('browse')}>
                    <Search className="w-4 h-4 mr-2" />
                    Browse Jobs
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {myApplications.map(app => (
                  <Card key={app.id}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-heading text-lg font-medium">{app.job_title}</h3>
                          <p className="text-sm text-muted-foreground mb-2">{app.company_name}</p>
                          <Badge variant={app.status === 'pending' ? 'secondary' : 
                                          app.status === 'accepted' ? 'default' : 'destructive'}>
                            {app.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Applied {format(new Date(app.created_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Job Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Post a Job</DialogTitle>
            <DialogDescription>
              Fill in the details to post a new job opportunity
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Job Title *</Label>
                <Input
                  placeholder="e.g., Veterinary Technician"
                  value={jobForm.title}
                  onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Company Name *</Label>
                <Input
                  placeholder="Your company name"
                  value={jobForm.company_name}
                  onChange={(e) => setJobForm({ ...jobForm, company_name: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={jobForm.category} onValueChange={(v) => setJobForm({ ...jobForm, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Job Type</Label>
                <Select value={jobForm.job_type} onValueChange={(v) => setJobForm({ ...jobForm, job_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {jobTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Location *</Label>
              <Input
                placeholder="City, State"
                value={jobForm.location}
                onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                id="remote-friendly"
                checked={jobForm.remote_friendly}
                onCheckedChange={(v) => setJobForm({ ...jobForm, remote_friendly: v })}
              />
              <Label htmlFor="remote-friendly">Remote-friendly position</Label>
            </div>
            
            <div>
              <Label>Description *</Label>
              <Textarea
                placeholder="Describe the role, responsibilities, and ideal candidate..."
                value={jobForm.description}
                onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                rows={4}
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Salary Min</Label>
                <Input
                  type="number"
                  placeholder="e.g., 40000"
                  value={jobForm.salary_min}
                  onChange={(e) => setJobForm({ ...jobForm, salary_min: e.target.value })}
                />
              </div>
              <div>
                <Label>Salary Max</Label>
                <Input
                  type="number"
                  placeholder="e.g., 60000"
                  value={jobForm.salary_max}
                  onChange={(e) => setJobForm({ ...jobForm, salary_max: e.target.value })}
                />
              </div>
              <div>
                <Label>Salary Type</Label>
                <Select value={jobForm.salary_type} onValueChange={(v) => setJobForm({ ...jobForm, salary_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yearly">Per Year</SelectItem>
                    <SelectItem value="hourly">Per Hour</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Requirements (one per line)</Label>
              <Textarea
                placeholder="2+ years experience&#10;Valid certification&#10;Excellent communication skills"
                value={jobForm.requirements}
                onChange={(e) => setJobForm({ ...jobForm, requirements: e.target.value })}
                rows={3}
              />
            </div>
            
            <div>
              <Label>Benefits (one per line)</Label>
              <Textarea
                placeholder="Health insurance&#10;401(k) matching&#10;Flexible schedule"
                value={jobForm.benefits}
                onChange={(e) => setJobForm({ ...jobForm, benefits: e.target.value })}
                rows={3}
              />
            </div>
            
            <div>
              <Label>Contact Email *</Label>
              <Input
                type="email"
                placeholder="hiring@company.com"
                value={jobForm.contact_email}
                onChange={(e) => setJobForm({ ...jobForm, contact_email: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateJob} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Post Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Job Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedJob?.title}</DialogTitle>
            <DialogDescription>
              {selectedJob?.company_name} • {selectedJob?.location}
            </DialogDescription>
          </DialogHeader>
          
          {selectedJob && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Badge>{selectedJob.job_type?.replace('_', ' ')}</Badge>
                <Badge variant="outline">{selectedJob.category?.replace('_', ' ')}</Badge>
                {selectedJob.remote_friendly && (
                  <Badge variant="secondary">
                    <Wifi className="w-3 h-3 mr-1" />
                    Remote OK
                  </Badge>
                )}
              </div>
              
              {formatSalary(selectedJob) && (
                <div className="flex items-center gap-2 text-lg font-medium text-green-600">
                  <DollarSign className="w-5 h-5" />
                  {formatSalary(selectedJob)}
                </div>
              )}
              
              <div>
                <h4 className="font-medium mb-2">Description</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {selectedJob.description}
                </p>
              </div>
              
              {selectedJob.requirements?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Requirements</h4>
                  <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                    {selectedJob.requirements.map((req, i) => (
                      <li key={`req-${i}-${req.slice(0,10)}`}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {selectedJob.benefits?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Benefits</h4>
                  <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                    {selectedJob.benefits.map((ben, i) => (
                      <li key={`ben-${i}-${ben.slice(0,10)}`}>{ben}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="text-sm text-muted-foreground">
                <p>Posted by: @{selectedJob.posted_by_username}</p>
                <p>Contact: {selectedJob.contact_email}</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
            {user && selectedJob && !hasApplied(selectedJob.id) && (
              <Button onClick={() => {
                setViewDialogOpen(false);
                setApplyDialogOpen(true);
              }}>
                <Send className="w-4 h-4 mr-2" />
                Apply Now
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Dialog */}
      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply for {selectedJob?.title}</DialogTitle>
            <DialogDescription>
              Submit your application to {selectedJob?.company_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Cover Letter</Label>
              <Textarea
                placeholder="Tell them why you're a great fit for this role..."
                value={applicationForm.cover_letter}
                onChange={(e) => setApplicationForm({ ...applicationForm, cover_letter: e.target.value })}
                rows={5}
              />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input
                placeholder="Your contact number"
                value={applicationForm.phone}
                onChange={(e) => setApplicationForm({ ...applicationForm, phone: e.target.value })}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Your profile information and email ({user?.email}) will be shared with the employer.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleApply} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobBoard;
